import { FormLabel, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { DatePicker, DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { SHORT_DATE_FORMAT } from '@npi/buddy-utils';
import { Dayjs } from 'dayjs';

interface CustomDatePickerProps {
    value: Dayjs;
    onChange: (value: Dayjs, context: PickerChangeHandlerContext<DateValidationError>) => void;
    minDate?: Dayjs;
    maxDate?: Dayjs;
    sx?: SxProps;
    disableFuture?: boolean;
    label?: string;
}

const defaultSx: SxProps = {
    '.MuiInputBase-input': {
        height: '9px',
    },
    '.MuiInputBase-root': { height: '40px' },
    '.MuiOutlinedInput-root': {
        '& fieldset': { borderColor: '#F39E4E' },
        '&:hover fieldset': {
            borderColor: '#F39E4E',
        },
    },
};

/**
 * A custom date range picker component that uses the MUI DatePicker component.
 *
 * @param {Dayjs} value - The current selected date.
 * @param {(value: Dayjs, context: PickerChangeHandlerContext<DateValidationError>) => void} onChange - A function to handle the date change event.
 * @param {Dayjs} [minDate] - The minimum allowed date. Defaults to null.
 * @param {Dayjs} [maxDate] - The maximum allowed date. Defaults to null.
 * @param {boolean} [disableFuture] - A flag to disable future dates. Defaults to true.
 *
 * @returns A custom date picker component with the specified parameters.
 */
const CustomDatePicker = ({
    value,
    onChange,
    minDate,
    maxDate,
    sx = defaultSx,
    disableFuture = true,
    label = '',
    ...props
}: CustomDatePickerProps & { 'data-testid'?: string }) => {
    const { palette } = useTheme();
    return (
        <>
            {label && (
                <FormLabel
                    sx={{
                        marginBottom: '8px',
                        color: palette.common.black,
                        fontWeight: 600,
                    }}
                >
                    {label}
                </FormLabel>
            )}
            <DatePicker
                disableFuture={disableFuture}
                minDate={minDate}
                maxDate={maxDate}
                format={SHORT_DATE_FORMAT}
                value={value}
                sx={sx}
                onChange={onChange}
                {...props}
                slots={{
                    textField: (params) => {
                        return (
                            <TextField
                                {...params}
                                sx={sx}
                                data-testid={props['data-testid']}
                                slotProps={{
                                    ...params.slotProps,
                                }}
                            />
                        );
                    },
                }}
            />
        </>
    );
};

export default CustomDatePicker;
